import { Badge, Code, Divider, Group, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { AppModal } from '../../shared/components/AppModal';
import type { LedgerEntryDto } from './api';

interface Props {
  entry: LedgerEntryDto | null;
  /** Resolved labels the row itself only carries as ids. */
  itemLabel: string;
  locationLabel: string;
  balanceTypeLabel: string;
  uomLabel: string;
  onClose: () => void;
}

/**
 * Everything about one ledger row.
 *
 * The grid can only carry the columns that fit, and a ledger row holds more than that — the lot,
 * the serial, the plate, the batch that groups a move's two halves. Those were all ids the screen
 * had no way to render, so in practice they did not exist for anybody using the product.
 *
 * WHO POSTED IT LEADS
 * It is the question every stock enquiry ends with, and until now the answer was nowhere in the
 * product. A row with no name is shown as unattributed rather than blank: the difference between
 * "a background job did this" and "the screen forgot to tell you" matters when somebody is trying
 * to work out how the count went wrong.
 */
export function LedgerEntryModal({
  entry,
  itemLabel,
  locationLabel,
  balanceTypeLabel,
  uomLabel,
  onClose,
}: Props) {
  const { t } = useTranslation();

  if (!entry) return null;

  const negative = entry.quantityDelta < 0;

  function Row({ label, children }: { label: string; children: React.ReactNode }) {
    return (
      <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
        <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>
          {label}
        </Text>
        <Text size="sm" ta="right" style={{ minWidth: 0, wordBreak: 'break-word' }}>
          {children}
        </Text>
      </Group>
    );
  }

  return (
    <AppModal opened onClose={onClose} title={t('inventory.entry.title')} width="sm">
      <Stack gap="xs">
        <Row label={t('inventory.entry.postedBy')}>
          {entry.performedByName ? (
            <Stack gap={0} align="flex-end">
              <Text size="sm" fw={500}>
                {entry.performedByName}
              </Text>
              {entry.performedByEmail && (
                <Text size="xs" c="dimmed">
                  {entry.performedByEmail}
                </Text>
              )}
            </Stack>
          ) : (
            <Text size="sm" c="dimmed" fs="italic">
              {t('inventory.entry.unattributed')}
            </Text>
          )}
        </Row>
        <Row label={t('inventory.when')}>{new Date(entry.performedAtUtc).toLocaleString()}</Row>

        <Divider my="xs" />

        <Row label={t('inventory.tranCode')}>
          <Group gap={6} justify="flex-end" wrap="nowrap">
            <Code>{entry.tranCode}</Code>
            <Text size="xs" c="dimmed">
              {entry.tranCodeName}
            </Text>
          </Group>
        </Row>
        <Row label={t('inventory.item')}>{itemLabel}</Row>
        <Row label={t('inventory.quantity')}>
          <Text component="span" size="sm" fw={600} c={negative ? 'red.7' : 'teal.7'}>
            {negative ? '' : '+'}
            {entry.quantityDelta.toLocaleString(undefined, { maximumFractionDigits: 5 })}
          </Text>{' '}
          <Text component="span" size="xs" c="dimmed">
            {uomLabel}
          </Text>
        </Row>
        <Row label={t('inventory.location')}>{locationLabel}</Row>
        <Row label={t('inventory.balanceType')}>{balanceTypeLabel}</Row>

        {/* Only when they apply. A run of "—" rows would bury the ones that say something. */}
        {entry.lotNumber && <Row label={t('inventory.entry.lot')}>{entry.lotNumber}</Row>}
        {entry.serialNumber && <Row label={t('inventory.entry.serial')}>{entry.serialNumber}</Row>}
        {entry.licensePlateNumber && (
          <Row label={t('inventory.entry.licensePlate')}>{entry.licensePlateNumber}</Row>
        )}

        {(entry.reason || entry.referenceType || entry.referenceId) && <Divider my="xs" />}

        {entry.reason && <Row label={t('inventory.reason')}>{entry.reason}</Row>}
        {(entry.referenceType || entry.referenceId) && (
          <Row label={t('inventory.reference')}>
            {[entry.referenceType, entry.referenceId].filter(Boolean).join(' ')}
          </Row>
        )}

        <Divider my="xs" />

        {entry.isBeginningBalance && (
          <Group justify="flex-end">
            <Badge variant="light" color="gray">
              {t('inventory.beginningBalance')}
            </Badge>
          </Group>
        )}
        {/* The two halves of a move share one batch, which is how somebody proves where stock went
            rather than inferring it from two rows a second apart. */}
        {entry.batchId && (
          <Row label={t('inventory.entry.batch')}>
            <Code>{entry.batchId}</Code>
          </Row>
        )}
        <Row label={t('inventory.entry.transactionId')}>
          <Code>{entry.inventoryTransactionId}</Code>
        </Row>
      </Stack>
    </AppModal>
  );
}
